// Cooperative Recast tiled builder, adapted from recast-navigation-js 0.44.0 (MIT; see vendor notice).
// Drive the public Recast/Detour primitives one tile at a time. Each tile's allocations stay owned here, even if a
// primitive throws, and a nonempty tile failure is reported instead of silently producing an incomplete mesh.
import { Raw, Recast, NavMesh, NavMeshParams, NavMeshCreateParams, VerticesArray, TrianglesArray, TriangleAreasArray,
  RecastBuildContext, RecastChunkyTriMesh, Detour, statusFailed, ChunkIdsArray, allocHeightfield, freeHeightfield,
  allocCompactHeightfield, freeCompactHeightfield, allocContourSet, freeContourSet, allocPolyMesh, freePolyMesh,
  allocPolyMeshDetail, freePolyMeshDetail, createHeightfield, markWalkableTriangles, rasterizeTriangles,
  filterLowHangingWalkableObstacles, filterLedgeSpans, filterWalkableLowHeightSpans, buildCompactHeightfield,
  erodeWalkableArea, buildDistanceField, buildRegions, buildContours, buildPolyMesh, buildPolyMeshDetail,
  createNavMeshData } from 'recast-navigation';
import { tiledNavMeshGeneratorConfigDefaults, getBoundingBox, buildTiledNavMeshRcConfig } from 'recast-navigation/generators';
import { NavigationUnavailable } from '../../../packages/holt-navigation/src/navigation.js';

function requireSuccess(ok) { if (!ok) throw new NavigationUnavailable(); }
function tileData(vertices, triangles, config, chunky, context, tile, cfg) {
  let clone, chunks, height, compact, contours, poly, detail, params, data, out;
  try {
    // Same fields as upstream's cloneRcConfig, but the allocation is owned even if a later setter throws.
    clone = new Raw.Module.rcConfig();
    for (let i = 0; i < 3; i++) { clone.set_bmin(i, config.get_bmin(i)); clone.set_bmax(i, config.get_bmax(i)); }
    for (const k of ['width', 'height', 'borderSize', 'tileSize', 'cs', 'ch', 'walkableSlopeAngle', 'walkableHeight',
      'walkableClimb', 'walkableRadius', 'maxEdgeLen', 'maxSimplificationError', 'minRegionArea', 'mergeRegionArea',
      'maxVertsPerPoly', 'detailSampleDist', 'detailSampleMaxError']) clone[k] = config[k];
    const bmin = [...tile.bmin], bmax = [...tile.bmax], border = clone.borderSize * clone.cs;
    bmin[0] -= border; bmin[2] -= border; bmax[0] += border; bmax[2] += border;
    for (let i = 0; i < 3; i++) { clone.set_bmin(i, bmin[i]); clone.set_bmax(i, bmax[i]); }
    context.resetTimers();
    height = allocHeightfield();
    requireSuccess(createHeightfield(context, height, clone.width, clone.height, bmin, bmax, clone.cs, clone.ch));
    // Check for saturation rather than using upstream's fixed buffer that can silently omit chunks.
    chunks = new ChunkIdsArray(); chunks.resize(513);
    const n = chunky.getChunksOverlappingRect([bmin[0], bmin[2]], [bmax[0], bmax[2]], chunks, 513);
    if (n >= 512) throw new NavigationUnavailable();
    if (!n) return null;
    for (let i = 0; i < n; i++) {
      const id = chunks.get(i), count = chunky.nodes(id).n;
      let nodeTriangles, areas;
      try {
        nodeTriangles = chunky.getNodeTris(id); areas = new TriangleAreasArray(); areas.resize(count);
        markWalkableTriangles(context, clone.walkableSlopeAngle, vertices, triangles.size, nodeTriangles, count, areas);
        requireSuccess(rasterizeTriangles(context, vertices, triangles.size, nodeTriangles, areas, count, height, clone.walkableClimb));
      } finally { try { nodeTriangles?.destroy(); } finally { areas?.destroy(); } }
    }
    filterLowHangingWalkableObstacles(context, clone.walkableClimb, height);
    filterLedgeSpans(context, clone.walkableHeight, clone.walkableClimb, height);
    filterWalkableLowHeightSpans(context, clone.walkableHeight, height);
    compact = allocCompactHeightfield();
    requireSuccess(buildCompactHeightfield(context, clone.walkableHeight, clone.walkableClimb, height, compact));
    requireSuccess(erodeWalkableArea(context, clone.walkableRadius, compact));
    requireSuccess(buildDistanceField(context, compact));
    requireSuccess(buildRegions(context, compact, clone.borderSize, clone.minRegionArea, clone.mergeRegionArea));
    contours = allocContourSet();
    requireSuccess(buildContours(context, compact, clone.maxSimplificationError, clone.maxEdgeLen, contours, Recast.RC_CONTOUR_TESS_WALL_EDGES));
    poly = allocPolyMesh(); requireSuccess(buildPolyMesh(context, contours, clone.maxVertsPerPoly, poly));
    detail = allocPolyMeshDetail(); requireSuccess(buildPolyMeshDetail(context, poly, compact, clone.detailSampleDist, clone.detailSampleMaxError, detail));
    for (let i = 0; i < poly.npolys(); i++) {
      if (poly.areas(i) === Recast.RC_WALKABLE_AREA) poly.setAreas(i, 0);
      if (poly.areas(i) === 0) poly.setFlags(i, 1);
    }
    params = new NavMeshCreateParams(); params.setPolyMeshCreateParams(poly); params.setPolyMeshDetailCreateParams(detail);
    params.setWalkableHeight(clone.walkableHeight * clone.ch); params.setWalkableRadius(clone.walkableRadius * clone.cs);
    params.setWalkableClimb(clone.walkableClimb * clone.ch); params.setCellSize(clone.cs); params.setCellHeight(clone.ch);
    params.setBuildBvTree(cfg.buildBvTree); params.setTileX(tile.x); params.setTileY(tile.y);
    const result = createNavMeshData(params); data = result.navMeshData;
    if (!result.success) { if (poly.npolys() === 0) return null; throw new NavigationUnavailable(); }
    out = data; data = null; return out;
  } finally {
    // Attempt every cleanup even when an earlier destroy fails.
    let failed = false;
    for (const f of [() => data?.destroy(), () => params?.destroy(), () => detail && freePolyMeshDetail(detail),
      () => poly && freePolyMesh(poly), () => contours && freeContourSet(contours),
      () => compact && freeCompactHeightfield(compact), () => height && freeHeightfield(height),
      () => chunks?.destroy(), () => { if (clone) Raw.destroy(clone); }, () => context.resetLog()]) {
      try { f(); } catch { failed = true; }
    }
    if (failed) { try { out?.destroy(); } catch {} throw new NavigationUnavailable(); }
  }
}

/** The caller must call finish() after all steps, or cancel() on every unsuccessful exit. */
export function createTiledBuilder(positions, indices, options) {
  let nav, vertices, triangles, context, chunky, config, closed = false, done = false, x = 0, y = 0;
  const close = (keepMesh) => {
    if (closed) return; closed = true; let failed = false;
    for (const f of [() => vertices?.destroy(), () => triangles?.destroy(), () => chunky?.destroy(),
      () => context?.destroy(), () => { if (config) Raw.destroy(config); }, () => { if (!keepMesh) nav?.destroy(); }]) {
      try { f(); } catch { failed = true; }
    }
    if (failed) { if (keepMesh) try { nav?.destroy(); } catch {} throw new NavigationUnavailable(); }
  };
  try {
    nav = new NavMesh(); vertices = new VerticesArray(); triangles = new TrianglesArray();
    context = new RecastBuildContext(); chunky = new RecastChunkyTriMesh(); vertices.copy(positions); triangles.copy(indices);
    const cfg = { ...tiledNavMeshGeneratorConfigDefaults, ...options,
      chunkyTriMeshTrisPerChunk: Math.max(256, Math.ceil(indices.length / 3 / 256)) };
    const bb = getBoundingBox(positions, indices);
    const info = buildTiledNavMeshRcConfig({ recastConfig: cfg, navMeshBounds: [bb.bbMin, bb.bbMax] }); config = info.config;
    const params = NavMeshParams.create({ orig: info.orig, tileWidth: cfg.tileSize * cfg.cs, tileHeight: cfg.tileSize * cfg.cs,
      maxTiles: info.maxTiles, maxPolys: info.maxPolysPerTile });
    try { if (!nav.initTiled(params)) throw new NavigationUnavailable(); } finally { Raw.destroy(params.raw); }
    if (!chunky.init(vertices, triangles, indices.length / 3, cfg.chunkyTriMeshTrisPerChunk)) throw new NavigationUnavailable();
    function step() {
      if (closed) throw new NavigationUnavailable(); if (done) return true;
      const bmin = [bb.bbMin[0] + x * info.tcs, bb.bbMin[1], bb.bbMin[2] + y * info.tcs];
      const bmax = [bb.bbMin[0] + (x + 1) * info.tcs, bb.bbMax[1], bb.bbMin[2] + (y + 1) * info.tcs];
      const data = tileData(vertices, triangles, config, chunky, context, { x, y, bmin, bmax }, cfg);
      if (data) {
        let added;
        try { added = nav.addTile(data, Detour.DT_TILE_FREE_DATA, 0); }
        catch (error) { try { data.destroy(); } catch {} throw error; }
        if (statusFailed(added.status)) { data.destroy(); throw new NavigationUnavailable(); }
      }
      if (++x === info.tileWidth) { x = 0; y++; } if (y === info.tileHeight) done = true;
      return done;
    }
    return { step, finish() { if (!done || closed) throw new NavigationUnavailable(); const result = nav; close(true); return result; },
      cancel() { close(false); }, total: info.tileWidth * info.tileHeight };
  } catch { try { close(false); } catch {} throw new NavigationUnavailable(); }
}
